import {
  compact,
  postWithTrace,
  get,
  handleAxiosError,
  TraceContext,
} from "./httpClient";

export interface GenerateFromOperationLogRequest {
  workspaceId: string;
  projectId: string;
  operationLogData?: any;
  viewId?: string | string[];
  /** 登録先（パネルで選んだ View / Component の ID）。バックエンドで Component 時に子ターゲットの親を上書き */
  registrationParentTestTargetId?: string;
  /** コンポーネントスコープ時のみ任意。指定時は操作ログをこの path に絞る */
  scopePathFilter?: string;
  additionalText?: string;
  templateType?: "none" | "repeat_elements"; // テンプレートID
  appSettings?: any;
  dryRun?: boolean;
  /**
   * 生成モード。ワイヤ値（"both"|"targets_only"|"usecases_only"）は backend(streamlit) 契約のため不変。
   * UI 表記は「ターゲット＋機能・手順 / ターゲットのみ / 機能・手順のみ」。
   * usecases_only は新モデルでは feature(機能) ＋ その main procedure(手順) の生成に相当する。
   */
  mode: "both" | "targets_only" | "usecases_only";
  ignoreExistingUsecases?: boolean; // 既存の機能・手順（旧ユースケース）情報を無視して単純作成
  pathViewMapping?: Record<string, string | null>; // ユーザーが選択したpath→viewIdのマッピング（"__NEW_VIEW__"は新規View）
}

export interface GenerateUsecasesFromTextRequest {
  viewId: string;
  title: string;
  description: string;
  workspaceId: string;
  projectId: string;
  usecaseId?: string;
  ignoreExistingUsecases?: boolean; // 既存ユースケース情報を無視して単純作成
  additionalInstructions?: string;
  appSettings?: any;
  dryRun?: boolean;
}

export interface GenerateUsecasesResponse {
  usecases: any[];
  message: string;
}

export interface PageResult {
  group_index: number;
  path: string;
  test_targets: any[];
  usecases: any[];
  is_new_view?: boolean;
  view_id?: string; // View項目のID（ユースケース登録時に使用）
}

export interface PathMatchCandidate {
  view_id: string;
  view_name: string;
  service_id?: string | null;
  service_name?: string | null;
  path?: string | null;
}

export interface PathMatchPreview {
  path: string;
  domain?: string | null;
  matched_view_id?: string | null;
  matched_view_name?: string | null;
  matched_service_id?: string | null;
  matched_service_name?: string | null;
  candidates: PathMatchCandidate[];
  note?: string;
}

export interface UnmatchedViewPreview {
  view_id: string;
  view_name: string;
  path?: string | null;
  service_id?: string | null;
  service_name?: string | null;
  note?: string;
}

export interface OperationLogPreview {
  path_matches: PathMatchPreview[];
  unmatched_views: UnmatchedViewPreview[];
}

export interface GenerateFromOperationLogResponse {
  page_results: PageResult[];
  message: string;
  preview?: OperationLogPreview;
  stats?: {
    totalGroups?: number;
    newViews?: number;
    updatedViews?: number;
    targetsGenerated?: number;
    usecasesGenerated?: number;
    successfulGroups?: number;
    failedGroups?: number;
  };
  /** Langfuse: pass to next /generate (retry) or /register so trace is linked for cost analysis */
  trace_id?: string;  // AIジョブモニタリング用トレースID
  traceparent?: string;  // W3C Trace Context
  /** 生成時の補足（例: トークン制限のためpagesを省略して再実行した旨） */
  generation_note?: string;
  /** generation_note内のリンク先URL（{{MANUAL_LINK}}の差し替え用） */
  generation_note_link_url?: string;
}

export interface UsecaseUpdate {
  view_test_target_id: string; // 対象のView項目のID
  usecases: any[]; // 追加・更新するユースケースのリスト
}

export interface RegisterTestTargetsRequest extends TraceContext {
  testTargets: any[];
  workspaceId: string;
  projectId: string;
  appSettings?: any;
  usecases?: UsecaseUpdate[]; // ユースケースの追加・更新
}

export interface FailedTestTargetItem {
  target_name?: string;
  reason: string;
  item_type?: "target" | "usecase";
  view_name?: string;
}

export interface RegisterTestTargetsResponse {
  registered_count: number;
  message: string;
  /**
   * #884: View 直下に作成された feature（旧「ユースケース」）＋main procedure の件数。
   * registered_count（テストターゲット）とは独立して集計されるため、
   * 機能だけを登録したケースでは registered_count が 0 でもこちらが正の値になる。
   * API のフィールド名は後方互換のため `usecases` のまま（表示上は「機能」）。
   */
  registered_usecases_count?: number;
  failed_items?: FailedTestTargetItem[];
  /**
   * spec-import 経路で削除に成功した件数。create/update の registered_count とは独立して集計する。
   * 0 / undefined の場合は削除なし。
   */
  deleted_count?: number;
  /**
   * spec-import 経路で、params 内の解決不能 ID 参照を batch 内未解決と判定して削除した件数。
   * 登録自体は成功するが、参照が silent に消えるため UI で警告 (severity=warning) を促す目的。
   * 0 / undefined の場合は警告対象外。
   */
  dropped_refs?: number;
}

export interface BillingLogRequest {
  traceId: string;
  projectId: string;
  workspaceId: string;
  billableUnitCount: number;
  feature?: string;
  message?: string;
  error?: string;
}

export interface BillingLogResponse {
  success: boolean;
  trace_id: string;
  message: string;
}

export interface TestTarget {
  name?: string;
  test_target_id?: string;
  type?: "Service" | "View" | "Section" | "Overlay" | "Item";
  description?: string;
  parent_test_target_id?: string;
  params?: any;
  operation?: string;
  display_order?: number;
  meta_info?: any;
  selected?: boolean; // UI用の選択状態
  /** 仕様書インポートのプレビュー専用: lib がパース/マッピングした生 params (登録用デフォルト注入前)。登録には使わない。 */
  specMappedParams?: Record<string, unknown>;
}

export class OperationLogService {
  private readonly baseUrl: string;
  private readonly useMockData: boolean;

  constructor(baseUrl: string = "", useMockData: boolean = false) {
    this.baseUrl = baseUrl;
    this.useMockData = useMockData;
  }

  async registerTestTargets(
    request: RegisterTestTargetsRequest
  ): Promise<RegisterTestTargetsResponse> {
    const payload = compact({
      test_targets: request.testTargets,
      workspace_id: request.workspaceId,
      project_id: request.projectId,
      app_settings: request.appSettings,
      usecases: request.usecases,
      trace_id: request.traceId, // AIジョブモニタリング用トレースID
    });

    // registerはtraceparentヘッダーを送らない
    // FastAPIInstrumentorが独立したトレースを自動生成する
    return postWithTrace<RegisterTestTargetsResponse>(
      this.baseUrl,
      "/operation-log/register",
      payload
    );
  }

  async generateFromOperationLog(
    request: GenerateFromOperationLogRequest
  ): Promise<GenerateFromOperationLogResponse> {
    // モックデータを使用する場合
    console.log("request:", request);
    console.log("this.useMockData:", this.useMockData);
    console.log("request.mode:", request.mode);
    if (this.useMockData && request.mode === "usecases_only") {
      return this.getMockUsecasesResponse();
    }

    const payload = compact({
      workspace_id: request.workspaceId,
      project_id: request.projectId,
      operation_log_data: request.operationLogData,
      view_id: request.viewId,
      registration_parent_test_target_id: request.registrationParentTestTargetId,
      scope_path_filter: request.scopePathFilter,
      mode: request.mode,
      additional_text: request.additionalText,
      template_type: request.templateType, // テンプレートIDを送信
      app_settings: request.appSettings,
      dry_run: request.dryRun,
      ignore_existing_usecases: request.ignoreExistingUsecases,
      path_view_mapping: request.pathViewMapping,
    });

    // 統合エンドポイント /operation-log/generate を使用
    return postWithTrace<GenerateFromOperationLogResponse>(
      this.baseUrl,
      "/operation-log/generate",
      payload
    );
  }

  async previewFromOperationLog(
    request: GenerateFromOperationLogRequest
  ): Promise<GenerateFromOperationLogResponse> {
    const payload = compact({
      workspace_id: request.workspaceId,
      project_id: request.projectId,
      operation_log_data: request.operationLogData,
      view_id: request.viewId,
      registration_parent_test_target_id: request.registrationParentTestTargetId,
      scope_path_filter: request.scopePathFilter,
      mode: request.mode,
      additional_text: request.additionalText,
      template_type: request.templateType,
      app_settings: request.appSettings,
      dry_run: request.dryRun,
      ignore_existing_usecases: request.ignoreExistingUsecases,
    });

    return postWithTrace<GenerateFromOperationLogResponse>(
      this.baseUrl,
      "/operation-log/preview",
      payload
    );
  }

  async generateUsecasesFromText(
    request: GenerateUsecasesFromTextRequest
  ): Promise<GenerateUsecasesResponse> {
    // additional_textにタイトルと説明を組み合わせる
    const additionalText = `タイトル: ${request.title}\n\n説明: ${request.description}${
      request.additionalInstructions
        ? `\n\n追加の指示: ${request.additionalInstructions}`
        : ""
    }`;

    // 統合エンドポイントを使用
    const generateRequest: GenerateFromOperationLogRequest = {
      workspaceId: request.workspaceId,
      projectId: request.projectId,
      viewId: request.viewId,
      mode: "usecases_only",
      additionalText: additionalText,
      appSettings: request.appSettings,
      dryRun: request.dryRun,
      ignoreExistingUsecases: request.ignoreExistingUsecases,
    };

    const response = await this.generateFromOperationLog(generateRequest);

    // レスポンスを GenerateUsecasesResponse 形式に変換
    const usecases: any[] = [];
    for (const pageResult of response.page_results) {
      usecases.push(...pageResult.usecases);
    }

    return {
      usecases: usecases,
      message: response.message,
    };
  }

  async healthCheck(): Promise<any> {
    return get(this.baseUrl, "/operation-log/health");
  }

  /**
   * 課金ログを出力する
   * SSTFへの登録完了後に呼び出し、課金対象のユニット数を記録する
   */
  async recordBillingLog(request: BillingLogRequest): Promise<BillingLogResponse> {
    const payload = compact({
      trace_id: request.traceId,
      project_id: request.projectId,
      workspace_id: request.workspaceId,
      billable_unit_count: request.billableUnitCount,
      feature: request.feature ?? "transitionGraph",
      message: request.message,
      error: request.error,
    });

    // billing-logはtraceparentヘッダーを送らない
    // FastAPIInstrumentorが独立したトレースを自動生成する
    return postWithTrace<BillingLogResponse>(
      this.baseUrl,
      "/operation-log/billing-log",
      payload
    );
  }

  private getMockUsecasesResponse(): Promise<GenerateFromOperationLogResponse> {
    // ユーザーから提供されたモックデータ
    const mockData: GenerateFromOperationLogResponse = {
      message: "1個のusecaseを生成しました",
      stats: {
        totalGroups: 2,
        newViews: 0,
        updatedViews: 2,
        targetsGenerated: 0,
        usecasesGenerated: 1,
        successfulGroups: 2,
        failedGroups: 0,
      },
      page_results: [
        {
          group_index: 0,
          path: "/",
          test_targets: [],
          usecases: [
            {
              usecase_id: "01939bc5-dd49-7507-8e9a-6a662abc0b14",
              name: "TOP画面の管理者メニューから新規会員登録画面への遷移",
              title: "TOP画面の管理者メニューから新規会員登録画面への遷移",
              description:
                "【目的・概要】\nTOP画面の管理者メニューから新規会員登録画面へ遷移する。\n\n【実行される操作の流れ】\nTOP画面にアクセスし、管理者メニューから新規会員登録の項目を選択してクリックする。\n\n【操作結果・変化】\n新規会員登録画面へ遷移する。\n\n【補足情報】\n管理者向けの機能である新規会員登録画面へのアクセス手順。",
              parameters: [],
              steps: [
                {
                  step: "TOP画面にアクセスする",
                  expect: "",
                  step_id: "01939bc5-dd49-70ad-925a-d976ec1e8a2a",
                  test_target_ids: ["01934a6b-9e4a-76e1-ac6a-25fd40c15705"],
                },
                {
                  step: "管理者メニューをクリックする",
                  expect: "",
                  step_id: "01939bc5-dd49-7a9e-b5c7-e4e81e93f23d",
                  test_target_ids: ["01934b70-d7fa-73e8-b8f1-bdf7d7e89f5c"],
                },
                {
                  step: "新規会員登録をクリックする",
                  expect: "",
                  step_id: "01939bc5-dd49-7a9e-b5c9-f1d0d4b03c44",
                  test_target_ids: ["01934b70-db44-70ca-8e5c-cf95c1f44156"],
                },
              ],
            },
          ],
          is_new_view: false,
          view_id: "01934a6b-9e4a-76e1-ac6a-25fd40c15705", // View項目のID
        },
        {
          group_index: 1,
          path: "/entry",
          test_targets: [],
          usecases: [],
          is_new_view: false,
          view_id: "01934b70-d7fa-73e8-b8f1-bdf7d7e89f5d", // View項目のID
        },
      ],
    };

    // 非同期処理をシミュレート（実際のAPIと同様に）
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log("モックデータを使用してユースケースを生成しました");
        resolve(mockData);
      }, 100);
    });
  }
}

// モックモードを環境変数で制御（デフォルトはfalse - 実際のAPIを使用）
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_USECASES === "true";

export const operationLogService = new OperationLogService("", USE_MOCK_DATA);
